import { AsyncPipe, DecimalPipe, NgClass } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  BbitArray,
  BbitLegalFilesResponse,
  BbitTime,
  BbitUtils,
  IBbitI18nSelectDataRow,
  IBbitLegalFile,
  IBbitPlaceBookingCalcResponse,
  IBbitPlaceBookingMetaResponse,
  IBbitSelectDataRow,
} from '@bbitgmbh/bbit.utils';
import { BbitWebShopFormatPricePipe } from '@bbitgmbh/webshop.sdk';
import { BbitOnlinePaymentService, BbitWebsiteService, I18nPipe, LuxonPipe } from '@bbitgmbh/website.sdk';
import { NgIcon } from '@ng-icons/core';
import { DateTime, Interval } from 'luxon';
import { BehaviorSubject, merge } from 'rxjs';
import { CompanionsService } from '../../account/companions/companions.service';
import { ComboboxComponent } from '../../helper/combobox/combobox.component';
import { LoadingComponent } from '../../helper/loading/loading.component';
import { PlaceBookingService } from '../booking.service';
import { BookingOverlayRef, IBbitBookingOverlayData } from './booking-overlay.ref';

@Component({
  selector: 'app-booking-overlay',
  templateUrl: './booking-overlay.component.html',
  styleUrl: './booking-overlay.component.scss',
  imports: [
    NgIcon,
    LoadingComponent,
    ReactiveFormsModule,
    FormsModule,
    NgClass,
    AsyncPipe,
    LuxonPipe,
    BbitWebShopFormatPricePipe,
    ComboboxComponent,
    DecimalPipe,
    I18nPipe,
  ],
})
export class BookingOverlayComponent {
  private _overlayRef = inject(BookingOverlayRef);
  private _website = inject(BbitWebsiteService);

  public data!: IBbitBookingOverlayData;
  private _onlinePayment = inject(BbitOnlinePaymentService);
  private _service = inject(PlaceBookingService);
  public errorOnPriceCalculation = signal(false);
  public displayType = new BehaviorSubject<'book' | 'success' | 'error' | 'loading' | 'redirectToPayment'>('loading');
  public usageType = new BehaviorSubject('');
  public canBeBooked = new BehaviorSubject(false);
  public durationRows = new BehaviorSubject<{ _id: string; text: string; interval: Interval }[]>([]);
  public selectedDuration = new BehaviorSubject<string | undefined>(undefined);
  public paymentMethods = new BehaviorSubject<IBbitI18nSelectDataRow[]>([]);
  private _allCompanions: IBbitSelectDataRow[] = [];
  public companions = new BehaviorSubject<IBbitSelectDataRow[]>([]);
  public selectedCompanions = new BehaviorSubject<IBbitSelectDataRow[]>([]);
  public selectedPaymentMethod = new BehaviorSubject<string | undefined>(undefined);
  public isCalculatingPrice = new BehaviorSubject(true);
  public companionService = inject(CompanionsService);
  public price = new BehaviorSubject<IBbitPlaceBookingCalcResponse | undefined>(undefined);
  public meta = new BehaviorSubject<IBbitPlaceBookingMetaResponse | undefined>(undefined);
  public legalFiles = new BehaviorSubject<BbitLegalFilesResponse>([]);
  public acceptedLegalFiles = signal<string[]>([]);

  constructor() {
    this.data = this._overlayRef.getData();

    const getIntervalText = (interval: Interval): string => {
      const duration = interval.toDuration();
      return `${interval.start?.toFormat('HH:mm')} - ${interval.end?.toFormat('HH:mm')} (${duration.toFormat('h:mm')})`;
    };

    const rows: { _id: string; text: string; interval: Interval }[] = [];
    const baseInterval = Interval.fromDateTimes(this.data.event.from, this.data.event.until);
    const defaultId = BbitUtils.makeId();
    this.selectedDuration.next(defaultId);
    rows.push({
      _id: defaultId,
      text: getIntervalText(baseInterval),
      interval: baseInterval,
    });

    if (this.data.option.allowDurationChange) {
      const calendarEvents: { from: DateTime; until: DateTime }[] = this.data.events.filter(
        (o) => o.calendar._id === this.data.event.calendar._id,
      );

      // inject closing time
      calendarEvents.push({
        from: this.data.closingTime,
        until: this.data.closingTime.endOf('day'),
      });

      const nextEvent = BbitArray.sortBy(calendarEvents, (o) => o.from.toISO() || '').find(
        (o) => o.from.toMillis() >= this.data.event.until.toMillis(),
      );

      const upcomingInterval = Interval.fromDateTimes(
        this.data.event.until,
        nextEvent?.from || this.data.event.from.plus({ hours: 1 }),
      );
      const durations = upcomingInterval.splitBy({ minutes: 30 });

      for (const o of durations) {
        const interval = Interval.fromDateTimes(this.data.event.from, o.end as DateTime);
        // const duration = interval.toDuration();
        // if (duration.as('minutes') > 60) {
        //   break;
        // }
        rows.push({
          _id: BbitUtils.makeId(),
          text: getIntervalText(interval),
          interval,
        });
      }
    }

    this.durationRows.next(rows);

    toObservable(this.acceptedLegalFiles)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.canBeBooked.next(this._canBeBooked());
      });

    merge(this.usageType, this.selectedPaymentMethod, this.selectedDuration)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.canBeBooked.next(false);
        if (!this.selectedPaymentMethod.value) {
          return;
        }
        if ((this.data?.option?.usageTypes?.length || 0) > 0) {
          if (!this.usageType.value) {
            return;
          }
        }
        this.calcPrice();
      });
  }

  async calcPrice(): Promise<void> {
    const body = this._getBookingBody();
    this.isCalculatingPrice.next(true);
    try {
      const response = await fetch(`${this._website.getBlApiUrl()}/placeBooking/calc`, {
        method: 'POST',
        headers: {
          Authorization: `${this._website.getAccessToken()}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        this.errorOnPriceCalculation.set(true);
        throw new Error('Booking failed');
      }
      this.errorOnPriceCalculation.set(false);
      const result = await response.json();
      this.canBeBooked.next(this._canBeBooked());
      this.price.next(result);
    } catch (err) {
      console.error(err);
      this.errorOnPriceCalculation.set(true);
      this.canBeBooked.next(this._canBeBooked());
      this.price.next(undefined);
    } finally {
      this.isCalculatingPrice.next(false);
    }
  }

  private _canBeBooked(): boolean {
    if (this.errorOnPriceCalculation()) {
      return false;
    }
    if (this.acceptedLegalFiles().length !== this.legalFiles.value.length) {
      return false;
    }
    if (this.selectedPaymentMethod.value === '#credit') {
      const newCreditBalance = (this.meta.value?.creditBalance || 0) - (this.price.value?.credits || 0);
      if (newCreditBalance < 0) {
        return false;
      }
    }
    return true;
  }

  async ngOnInit(): Promise<void> {
    await Promise.all([this._loadMeta(), this._loadCompanions(), this._loadLegalFiles()]);
    this.displayType.next('book');
  }

  private async _loadMeta(): Promise<void> {
    const meta = await this._service.getMeta({ place: this.data.event.calendar._id });
    this.meta.next(meta);
    this.paymentMethods.next(meta.allowedPaymentMethods);
    if (!this.selectedPaymentMethod.value) {
      this.selectedPaymentMethod.next(meta.allowedPaymentMethods?.[0]?._id);
    }
  }

  private async _loadCompanions(): Promise<void> {
    const companions = await this.companionService.list();
    this.companions.next(companions.filter((o) => !this.selectedCompanions.value.some((c) => c._id === o._id)));
    this.selectedCompanions.next(this.selectedCompanions.value.filter((o) => companions.some((c) => c._id === o._id)));
    this._allCompanions = companions;
  }

  private async _loadLegalFiles(): Promise<void> {
    const files = await this._service.getLegalFiles();
    this.legalFiles.next(files.filter((o) => o.isRequiredOnCheckout));
  }

  public getDownloadUrl(file: IBbitLegalFile): string {
    return this._service.getDownloadUrl(file);
  }

  public toggleAcceptedLegalFile(id: string): void {
    if (this.acceptedLegalFiles().includes(id)) {
      this.acceptedLegalFiles.set(this.acceptedLegalFiles().filter((o) => o !== id));
    } else {
      this.acceptedLegalFiles.set([...this.acceptedLegalFiles(), id]);
    }
  }

  public addCompanion(companion: IBbitSelectDataRow): void {
    this.selectedCompanions.next([...this.selectedCompanions.value, companion]);
    this.companions.next(
      this._allCompanions.filter((o) => !this.selectedCompanions.value.some((c) => c._id === o._id)),
    );
  }

  public async createCompanion() {
    await this.companionService.addCompanion();
    await this._loadCompanions();
  }

  public removeCompanion(companion: IBbitSelectDataRow): void {
    this.selectedCompanions.next(this.selectedCompanions.value.filter((o) => o._id !== companion._id));
    this.companions.next(
      this._allCompanions.filter((o) => !this.selectedCompanions.value.some((c) => c._id === o._id)),
    );
  }

  close(reload = false) {
    this._overlayRef.close(reload);
  }

  // TODO types
  // biome-ignore lint/suspicious/noExplicitAny: required
  private _getBookingBody(): any {
    let from = BbitTime.fromDateTime(this.data.event.from).toISOTime();
    let until = BbitTime.fromDateTime(this.data.event.until).toISOTime();
    if (this.selectedDuration.value) {
      const duration = this.durationRows.value.find((o) => o._id === this.selectedDuration.value);
      from = BbitTime.fromDateTime(duration?.interval.start || this.data.event.from).toISOTime();
      until = BbitTime.fromDateTime(duration?.interval.end || this.data.event.until).toISOTime();
    }
    return {
      date: this.data.event.from,
      place: this.data.event.calendar._id,
      paymentMethod: this.selectedPaymentMethod.value,
      from,
      until,
      usageType: this.usageType.value,
      companions: this.selectedCompanions.value.map((o) => o._id),
    };
  }

  async book(): Promise<void> {
    if (!this.canBeBooked.value) {
      return;
    }
    this.displayType.next('loading');

    const body = this._getBookingBody();
    try {
      const response = await fetch(`${this._website.getBlApiUrl()}/placeBooking/book`, {
        method: 'POST',
        headers: {
          Authorization: `${this._website.getAccessToken()}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error('Booking failed');
      }
      const result = await response.json();

      if (result.isOnlinePayment) {
        if (result.invoiceId) {
          this._onlinePayment.pay({
            invoice: result.invoiceId,
            redirectUrl: `${window.location.origin}/shop/order-result`,
          });
          this.displayType.next('redirectToPayment');
          return;
        }
      }

      this.displayType.next('success');
    } catch (err) {
      this.displayType.next('error');
      console.error(err);
    }
  }



  public async openCreditTopUpOverlay(): Promise<void> {
    const result = await this._overlayRef.service.openCreditTopUp();
    if (result.reload) {
      this.isCalculatingPrice.next(true);
      await this.calcPrice();
      await this._loadMeta();
    }
  }
}
